'use client';

import TrashCanSvg from '@/assets/svgs/trash-can.svg';
import {
  setInitialMessageMedia,
  setIsShowingMediaManager,
} from '@/containers/Jobs/redux/jobEditors.slice';
import { useDeleteMedia } from '@/containers/MediaManager/api';
import { selectMedia } from '@/containers/MediaManager/redux/mediaManager.selectors';
import { removeMedia } from '@/containers/MediaManager/redux/mediaManager.slice';
import { formatFileSize } from '@/containers/MediaManager/utils/fileHelpers';
import { getIdentityNameById } from '@/containers/MediaManager/utils/identityHelpers';
import { Modals } from '@/containers/Modals/all-modals';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { selectAccountIdentities } from '@/lib/redux/app.selectors';
import { useAppDispatch } from '@/lib/redux/store';
import { formatRelativeTime } from '@/shared/utils/formatRelativeTime';

import MediaIconSvg from '@/assets/svgs/main/media-filled.svg';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { MediaViewerModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import VCardView from './VCardView';

const modalName = Modals.MediaViewerModal;

interface MediaViewerModalProps extends ModalProps {
  className?: string;
  params?: MediaViewerModalParams;
}

const MediaViewerModal = ({
  className,
  params,
  variant = 'default',
}: MediaViewerModalProps) => {
  const lightboxContainerRef = useRef<HTMLDivElement>(null);
  const { isModalOpen, onClose } = useModals({
    modalName,
    shouldCloseOnEscape: true,
  });
  const dispatch = useAppDispatch();
  const identities = useSelector(selectAccountIdentities);
  const allMedia = useSelector(selectMedia);
  const { showError, showSuccess } = useToast();

  // Modal Specific details
  const { mediaId, jobId } = params || {};
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Find the current media item from the Redux store
  const currentMediaData = allMedia.find((item) => item.media_id === mediaId);

  // Build media viewer items from all active media (excluding vCards)
  const mediaViewerItems = allMedia
    .filter((item) => item.status === 'ACTIVE' && item.media_type !== 'VCARD')
    .map((item) => ({
      mediaType: item.media_type === 'IMAGE' ? ('image' as const) : ('video' as const),
      src: item.preview_url,
      title: item.title,
    }));

  // Find the current item's index in the active media array (excluding vCards)
  const initialIndex = allMedia
    .filter((item) => item.status === 'ACTIVE' && item.media_type !== 'VCARD')
    .findIndex((item) => item.media_id === mediaId);

  const currentItem = mediaViewerItems[Math.max(0, initialIndex)] || null;

  const isImage = currentItem?.mediaType === 'image';
  const isVideo = currentItem?.mediaType === 'video';
  const isVCard = currentMediaData?.media_type === 'VCARD';
  const isFromTextEditor = !!jobId;

  // Delete mutation
  const { mutate: deleteMedia, isPending: isDeleting } = useDeleteMedia();

  // Get identity name for display
  const identityName = getIdentityNameById(
    currentMediaData?.identity_id || '',
    identities
  );

  const handleDelete = useCallback(() => {
    if (!currentMediaData?.media_id) return;

    deleteMedia(currentMediaData.media_id, {
      onSuccess: () => {
        // Update Redux state first (fallback data source)
        dispatch(removeMedia(currentMediaData.media_id));

        // MediaList will auto-detect Redux changes and refetch

        // Show success message
        showSuccess('Media deleted successfully.');

        // Close the modal
        onClose();
      },
      onError: (error: unknown) => {
        console.error('Failed to delete media:', error);

        // Extract error message from API response
        let errorMessage = 'Failed to delete media. Please try again.';

        if (error && typeof error === 'object' && 'response' in error) {
          const apiError = error as {
            response?: { data?: { Error?: string } };
            message?: string;
          };
          errorMessage =
            apiError.response?.data?.Error || apiError.message || errorMessage;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        // Show error toast
        showError(errorMessage);
      },
    });
  }, [
    currentMediaData?.media_id,
    deleteMedia,
    dispatch,
    showError,
    showSuccess,
    onClose,
  ]);

  const handleInsertMedia = useCallback(() => {
    if (!isFromTextEditor || !currentMediaData) return;

    // Set media in the message
    dispatch(
      setInitialMessageMedia({
        jobId: jobId || '',
        media: currentMediaData,
      })
    );
    // Close the modal
    onClose();
    // Close the mini Media Manager if it's open
    dispatch(setIsShowingMediaManager({ jobId: jobId || '', isShowing: false }));
  }, [isFromTextEditor, currentMediaData, dispatch, jobId, onClose]);

  useEffect(() => {
    const handleKeyEvent = (e: Event) => {
      if (e instanceof KeyboardEvent) {
        if (e.key === 'Escape' && showDeleteConfirm) {
          e.stopPropagation(); // Prevent global handler from closing modal
          setShowDeleteConfirm(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyEvent);
    };
  }, [showDeleteConfirm]);

  return (
    <div ref={lightboxContainerRef} className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}
        shouldCloseOnBackgroundClick>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="url"
            icon={<MediaIconSvg className="h-5 w-5" />}
            title={
              isVCard
                ? currentMediaData?.title || 'Media Preview'
                : currentItem?.title || currentMediaData?.title || 'Media Preview'
            }
          />

          {/* Content */}
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Media Preview Panel */}
            <div className="flex min-h-0 flex-[2] items-center justify-center bg-gray-900">
              {/* Loading Spinner */}
              {!currentItem && !isVCard && (
                <span className="loading loading-spinner loading-lg text-white" />
              )}

              {/* Image or Video */}
              {currentItem && !isVCard && (
                <>
                  {isImage && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={currentItem.src}
                      alt={currentItem.title || 'Media preview'}
                      className="max-h-full max-w-full object-contain"
                    />
                  )}

                  {isVideo && (
                    <video
                      className="max-h-full max-w-full object-contain"
                      src={currentItem.src}
                      controls={true}
                      autoPlay={false}
                      loop={false}
                    />
                  )}
                </>
              )}

              {/* vCard */}
              {isVCard && currentMediaData && (
                <VCardView
                  url={currentMediaData.preview_url}
                  altText={currentMediaData.title}
                />
              )}
            </div>

            {/* Details Panel */}
            <div className="dark:bg-base-100 flex min-h-0 flex-1 flex-col bg-white">
              {/* Media Information */}
              <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6">
                {/* Upload Date */}
                {currentMediaData?.created_date && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Uploaded:</span>{' '}
                    {formatRelativeTime(currentMediaData.created_date)}
                  </div>
                )}

                {/* Identity */}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Identity:</span> {identityName}
                </div>

                {/* File Size */}
                {currentMediaData?.size_bytes && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Size:</span>{' '}
                    {formatFileSize(currentMediaData.size_bytes)}
                  </div>
                )}

                {/* Insert Media Button - Only show if from text editor */}
                {isFromTextEditor && (
                  <button onClick={handleInsertMedia} className="btn btn-primary">
                    Insert Media
                  </button>
                )}

                {/* Spacer to push delete button to bottom */}
                <div className="flex-1" />

                {/* Delete Button */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  className="btn btn-error w-full">
                  <TrashCanSvg className="h-4 w-4" />
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="dark:bg-base-100 rounded-lg bg-white p-6 shadow-xl">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-200">
                  Delete Media
                </h3>
                <p className="mb-6 text-gray-600 dark:text-gray-400">
                  Are you sure you want to delete &ldquo;
                  {currentItem?.title || currentMediaData?.title || 'this media'}&rdquo;?
                  This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="btn btn-outline">
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      handleDelete();
                    }}
                    disabled={isDeleting}
                    className="btn btn-error">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default MediaViewerModal;
